// ============================================================
// EWFL Game Data & Player Generation
// ============================================================

export const ROSTER_SIZE = 25;
export const LINEUP_SIZE = 11;
export const BENCH_SIZE = ROSTER_SIZE - LINEUP_SIZE;
export const JWFL_VERSION = 4;
export const START_YEAR = 2026;

// Japanese first names (modern, common in Japan)
const ENGLISH_FIRST_NAMES = [
  'Sora', 'Yuto', 'Haruki', 'Ren', 'Riku', 'Hayato', 'Kaito', 'Daiki', 'Yuki', 'Shota',
  'Takumi', 'Ryo', 'Kenta', 'Yusuke', 'Hiroto', 'Naoki', 'Kazuki', 'Taiga', 'Masaki', 'Itsuki',
  'Tatsuya', 'Akira', 'Shun', 'Koki', 'Yuma', 'Asahi', 'Rin', 'Tomoya', 'Kohei', 'Daisuke',
  'Takashi', 'Kenji', 'Hideo', 'Junpei', 'Toshiro', 'Makoto', 'Ryota', 'Shogo', 'Takeshi', 'Yoshiki',
  'Eiji', 'Hiroshi', 'Satoshi', 'Ichiro', 'Goro', 'Jun', 'Kei', 'Sho', 'Tsubasa', 'Yamato'
];
const ENGLISH_LAST_NAMES = [
  'Sato', 'Suzuki', 'Takahashi', 'Tanaka', 'Watanabe', 'Ito', 'Yamamoto', 'Nakamura', 'Kobayashi', 'Kato',
  'Yoshida', 'Yamada', 'Sasaki', 'Matsumoto', 'Inoue', 'Kimura', 'Hayashi', 'Saito', 'Shimizu', 'Yamazaki',
  'Mori', 'Abe', 'Ikeda', 'Hashimoto', 'Yamashita', 'Ishikawa', 'Nakajima', 'Maeda', 'Fujita', 'Ogawa',
  'Goto', 'Okada', 'Hasegawa', 'Murakami', 'Kondo', 'Ishii', 'Sakamoto', 'Endo', 'Aoki', 'Fujii',
  'Nishimura', 'Fukuda', 'Ota', 'Miura', 'Fujiwara', 'Okamoto', 'Matsuda', 'Nakagawa', 'Harada', 'Ono'
];
// Foreign-born J.League imports (predominantly Brazilian + a sprinkle of other nationalities)
const FOREIGN_FIRST_NAMES = [
  'Lucas', 'Gabriel', 'Matheus', 'Diego', 'Carlos', 'Anderson', 'Felipe', 'Ricardo', 'Bruno', 'Thiago',
  'Marcos', 'Renato', 'Leandro', 'Rodrigo', 'Caio', 'Wellington', 'Patric', 'Marcinho',
  'Mateusz', 'Andrés', 'Cristian', 'Sebastián',
  'Han', 'Min-jun', 'Tae-hwan', 'Ji-woo',
  'Mohammed', 'Hassan', 'Omar'
];
const FOREIGN_LAST_NAMES = [
  'Silva', 'Santos', 'Oliveira', 'Costa', 'Ferreira', 'Pereira', 'Almeida', 'Rodrigues', 'Lima', 'Gomes',
  'Souza', 'Carvalho', 'Ribeiro', 'Alves', 'Martins', 'Fernandes', 'Andrade', 'Junior',
  'García', 'Martínez', 'López', 'Sánchez',
  'Kim', 'Lee', 'Park', 'Choi'
];

const NATIONALITIES = [
  { country: 'Japan',      flag: '🇯🇵', weight: 70 },
  { country: 'Brazil',     flag: '🇧🇷', weight: 12 },
  { country: 'South Korea', flag: '🇰🇷', weight: 5 },
  { country: 'Spain',      flag: '🇪🇸', weight: 3 },
  { country: 'Argentina',  flag: '🇦🇷', weight: 2 },
  { country: 'Germany',    flag: '🇩🇪', weight: 2 },
  { country: 'England',    flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', weight: 2 },
  { country: 'France',     flag: '🇫🇷', weight: 2 },
  { country: 'Portugal',   flag: '🇵🇹', weight: 1 },
  { country: 'Italy',      flag: '🇮🇹', weight: 1 }
];

// 20 Japanese cities + iconic team naming (loosely inspired by J.League conventions)
const ENGLISH_CITIES = [
  'Tokyo', 'Yokohama', 'Osaka', 'Nagoya', 'Sapporo',
  'Kobe', 'Kyoto', 'Fukuoka', 'Hiroshima', 'Sendai',
  'Chiba', 'Saitama', 'Niigata', 'Kawasaki', 'Kashima',
  'Urawa', 'Shimizu', 'Kashiwa', 'Omiya', 'Oita'
];
const TEAM_NICKNAMES = [
  'Tokyo Verdy', 'F·Marinos', 'Cerezo', 'Grampus', 'Consadole',
  'Vissel', 'Sanga', 'Avispa', 'Sanfrecce', 'Vegalta',
  'JEF United', 'Reds', 'Albirex', 'Frontale', 'Antlers',
  'Diamonds', 'S-Pulse', 'Reysol', 'Ardija', 'Trinita'
];

// Position compatibility groups
export const POSITION_GROUPS = {
  GK:  ['GK'],
  CB:  ['CB', 'SW'],
  SW:  ['SW', 'CB'],
  LB:  ['LB', 'LWB', 'LM'],
  RB:  ['RB', 'RWB', 'RM'],
  LWB: ['LWB', 'LB', 'LM'],
  RWB: ['RWB', 'RB', 'RM'],
  CDM: ['CDM', 'CM'],
  CM:  ['CM', 'CDM', 'CAM'],
  CAM: ['CAM', 'CM', 'SS'],
  LM:  ['LM', 'LW', 'LWB'],
  RM:  ['RM', 'RW', 'RWB'],
  LW:  ['LW', 'LM', 'LF'],
  RW:  ['RW', 'RM', 'RF'],
  ST:  ['ST', 'CF', 'SS'],
  CF:  ['CF', 'ST'],
  SS:  ['SS', 'CAM', 'ST'],
  LF:  ['LF', 'LW', 'ST'],
  RF:  ['RF', 'RW', 'ST']
};

export const FORMATIONS = [
  { name: '4-3-3', positions: ['GK', 'LB', 'CB', 'CB', 'RB', 'CM', 'CM', 'CM', 'LW', 'ST', 'RW'] },
  { name: '4-4-2', positions: ['GK', 'LB', 'CB', 'CB', 'RB', 'LM', 'CM', 'CM', 'RM', 'ST', 'ST'] },
  { name: '4-2-3-1', positions: ['GK', 'LB', 'CB', 'CB', 'RB', 'CDM', 'CDM', 'LW', 'CAM', 'RW', 'ST'] },
  { name: '3-5-2', positions: ['GK', 'CB', 'CB', 'CB', 'LWB', 'CM', 'CDM', 'CM', 'RWB', 'ST', 'ST'] },
  { name: '4-1-2-1-2', positions: ['GK', 'LB', 'CB', 'CB', 'RB', 'CDM', 'CM', 'CM', 'CAM', 'ST', 'ST'] },
  { name: '5-3-2', positions: ['GK', 'LWB', 'CB', 'CB', 'CB', 'RWB', 'CM', 'CM', 'CM', 'ST', 'ST'] },
  { name: '4-5-1', positions: ['GK', 'LB', 'CB', 'CB', 'RB', 'LM', 'CM', 'CDM', 'CM', 'RM', 'ST'] },
  { name: '3-4-3', positions: ['GK', 'CB', 'CB', 'CB', 'LM', 'CM', 'CM', 'RM', 'LW', 'ST', 'RW'] }
];

export const POSITION_COORDS = {
  'GK': { x: 50, y: 90 }, 'LB': { x: 15, y: 70 }, 'CB': { x: 35, y: 75 }, 'CB2': { x: 65, y: 75 }, 'CB3': { x: 50, y: 78 },
  'RB': { x: 85, y: 70 }, 'LWB': { x: 10, y: 60 }, 'RWB': { x: 90, y: 60 }, 'SW': { x: 50, y: 82 },
  'CDM': { x: 40, y: 60 }, 'CDM2': { x: 60, y: 60 }, 'CM': { x: 35, y: 50 }, 'CM2': { x: 50, y: 50 }, 'CM3': { x: 65, y: 50 },
  'CAM': { x: 50, y: 35 }, 'LM': { x: 15, y: 45 }, 'RM': { x: 85, y: 45 }, 'LW': { x: 15, y: 25 }, 'RW': { x: 85, y: 25 },
  'CF': { x: 50, y: 20 }, 'ST': { x: 40, y: 15 }, 'ST2': { x: 60, y: 15 }, 'SS': { x: 50, y: 25 },
  'LF': { x: 35, y: 18 }, 'RF': { x: 65, y: 18 }
};

const POSITION_ROW = (pos) => {
  if (pos === 'GK') return 'GK';
  if (['CB', 'LB', 'RB', 'SW'].includes(pos)) return 'DEF';
  if (['LWB', 'RWB'].includes(pos)) return 'WB';
  if (pos === 'CDM') return 'CDM';
  if (['CM', 'LM', 'RM'].includes(pos)) return 'CM';
  if (pos === 'CAM' || pos === 'SS') return 'CAM';
  if (['LW', 'RW', 'LF', 'RF'].includes(pos)) return 'WIDE_ATT';
  return 'ATT';
};
const ROW_Y = { GK: 92, DEF: 74, WB: 62, CDM: 55, CM: 45, CAM: 33, WIDE_ATT: 22, ATT: 14 };
const SIDE_OF = (pos) => {
  if (pos.startsWith('L')) return 0;
  if (pos.startsWith('R')) return 2;
  return 1;
};

export const getFormationCoords = (formationName) => {
  const formation = FORMATIONS.find(f => f.name === formationName) || FORMATIONS[0];
  const groups = {};
  formation.positions.forEach((pos, idx) => {
    const row = POSITION_ROW(pos);
    if (!groups[row]) groups[row] = [];
    groups[row].push({ pos, idx });
  });
  const coords = new Array(formation.positions.length);
  Object.entries(groups).forEach(([row, players]) => {
    const sorted = [...players].sort((a, b) => {
      const sa = SIDE_OF(a.pos);
      const sb = SIDE_OF(b.pos);
      if (sa !== sb) return sa - sb;
      return a.idx - b.idx;
    });
    const n = sorted.length;
    sorted.forEach((p, i) => {
      const x = (100 / (n + 1)) * (i + 1);
      coords[p.idx] = { position: p.pos, x, y: ROW_Y[row] ?? 50, playerIndex: p.idx };
    });
  });
  return coords;
};

export const buildLineupForFormation = (team) => {
  const formation = FORMATIONS.find(f => f.name === team?.formation) || FORMATIONS[0];
  const all = (team?.players || []).slice();
  const healthy = all.filter(p => !p.injuredWeeks || p.injuredWeeks <= 0);
  const used = new Set();
  const lineup = [];

  const pickBest = (candidates) => {
    const sorted = candidates.filter(p => !used.has(p.number)).sort((a, b) => (b.overall || 0) - (a.overall || 0));
    return sorted[0] || null;
  };

  formation.positions.forEach(slot => {
    const group = POSITION_GROUPS[slot] || [slot];
    const isGkSlot = slot === 'GK';
    let picked = null;
    for (const posCandidate of group) {
      const pool = healthy.filter(p => {
        if (isGkSlot) return p.position === 'GK';
        if (p.position === 'GK') return false;
        return p.position === posCandidate;
      });
      picked = pickBest(pool);
      if (picked) break;
    }
    if (!picked && !isGkSlot) {
      picked = pickBest(healthy.filter(p => p.position !== 'GK'));
    }
    if (!picked) picked = pickBest(healthy);
    if (picked) { used.add(picked.number); lineup.push(picked.number); }
  });

  const remainHealthy = all.filter(p => !used.has(p.number) && (!p.injuredWeeks || p.injuredWeeks <= 0));
  const remainInjured = all.filter(p => !used.has(p.number) && p.injuredWeeks > 0);
  const bench = [...remainHealthy, ...remainInjured].slice(0, BENCH_SIZE).map(p => p.number);

  return { lineup, bench };
};

const getRandomNationality = () => {
  const totalWeight = NATIONALITIES.reduce((sum, n) => sum + n.weight, 0);
  let random = Math.random() * totalWeight;
  for (const n of NATIONALITIES) { random -= n.weight; if (random <= 0) return n; }
  return NATIONALITIES[0];
};
const generatePlayerName = (nationality) => {
  const isHome = nationality.country === 'Japan';
  if (isHome) {
    return `${ENGLISH_FIRST_NAMES[Math.floor(Math.random() * ENGLISH_FIRST_NAMES.length)]} ${ENGLISH_LAST_NAMES[Math.floor(Math.random() * ENGLISH_LAST_NAMES.length)]}`;
  }
  return `${FOREIGN_FIRST_NAMES[Math.floor(Math.random() * FOREIGN_FIRST_NAMES.length)]} ${FOREIGN_LAST_NAMES[Math.floor(Math.random() * FOREIGN_LAST_NAMES.length)]}`;
};
const generateAge = (position) => {
  const isGK = position === 'GK';
  const minAge = isGK ? 20 : 17;
  const maxAge = isGK ? 38 : 35;
  return Math.floor(Math.random() * (maxAge - minAge + 1)) + minAge;
};
const generatePotential = (overall, age) => {
  // Tighter potential distribution — only ~15% of players cross 90
  // Old code gave +5..+19 boost which made nearly everyone 90+. New formula:
  //  - veterans (32+): potential = current overall (no growth)
  //  - 30-31: +0..+2
  //  - 25-29: +1..+5
  //  - 21-24: +2..+9
  //  - 18-20: +3..+13
  //  - <=17:  +4..+16 (the "wonder kid" range)
  if (age >= 32) return overall;
  let lo = 0, hi = 0;
  if (age >= 30) { lo = 0; hi = 2; }
  else if (age >= 25) { lo = 1; hi = 5; }
  else if (age >= 21) { lo = 2; hi = 9; }
  else if (age >= 18) { lo = 3; hi = 13; }
  else { lo = 4; hi = 16; }
  // Soft cap: only ~12% chance to add a "hot prospect" bonus that pushes near 99
  const hotShot = Math.random() < 0.12 ? Math.floor(Math.random() * 4) : 0;
  const boost = lo + Math.floor(Math.random() * (hi - lo + 1)) + hotShot;
  // Hard ceiling 95 unless they were already a star (overall >= 88)
  const ceiling = overall >= 88 ? 99 : overall >= 82 ? 94 : 91;
  return Math.min(ceiling, Math.max(overall, overall + boost));
};
const randomOverall = () => Math.floor(Math.random() * 21) + 60; // 60-80

// ============================================================
// Per-Competition Stats Model
// ============================================================
export const EMPTY_COMP_STATS = () => ({
  goals: 0, assists: 0, yellowCards: 0, redCards: 0, matchesPlayed: 0, cleanSheets: 0
});
export const EMPTY_STATS = () => ({
  league: EMPTY_COMP_STATS(),
  cup: EMPTY_COMP_STATS(),
  championsLeague: EMPTY_COMP_STATS(),
  europaLeague: EMPTY_COMP_STATS(),
  conferenceLeague: EMPTY_COMP_STATS(),
  cwc: EMPTY_COMP_STATS(),
  worldCup: EMPTY_COMP_STATS()
});

export const COMPETITIONS = [
  { key: 'league', label: 'J.League', color: '#00E5FF' },
  { key: 'cup', label: "Emperor's Cup", color: '#FF006E' },
  { key: 'asianCup', label: 'Asian Cup', color: '#CCFF00' },
  { key: 'cwc', label: 'FIFA Club World Cup', color: '#FFB400' },
  { key: 'worldCup', label: 'FIFA World Cup', color: '#FF4D00' }
];

export const getPlayerStat = (player, statKey, competition = 'total') => {
  if (!player?.stats) return 0;
  if (competition === 'total') {
    return COMPETITIONS.reduce((sum, c) => sum + (player.stats[c.key]?.[statKey] || 0), 0);
  }
  return player.stats[competition]?.[statKey] || 0;
};

export const migratePlayer = (p) => {
  if (!p) return p;
  if (p.stats?.league && p.stats?.championsLeague && p.stats?.europaLeague && p.stats?.conferenceLeague) return p;
  const legacy = p.stats || {};
  return {
    ...p,
    stats: {
      league: p.stats?.league || {
        goals: legacy.goals || 0,
        assists: legacy.assists || 0,
        yellowCards: legacy.yellowCards || 0,
        redCards: legacy.redCards || 0,
        matchesPlayed: legacy.matchesPlayed || 0,
        cleanSheets: legacy.cleanSheets || 0
      },
      cup: p.stats?.cup || EMPTY_COMP_STATS(),
      championsLeague: p.stats?.championsLeague || p.stats?.asianCup || EMPTY_COMP_STATS(),
      europaLeague: p.stats?.europaLeague || EMPTY_COMP_STATS(),
      conferenceLeague: p.stats?.conferenceLeague || EMPTY_COMP_STATS(),
      cwc: p.stats?.cwc || EMPTY_COMP_STATS(),
      worldCup: p.stats?.worldCup || EMPTY_COMP_STATS()
    },
    seasonStartOverall: p.seasonStartOverall ?? p.overall
  };
};
export const migrateTeam = (t) => {
  if (!t) return t;
  const players = (t.players || []).map(migratePlayer);
  const teamWithFormation = { ...t, players, formation: t.formation || FORMATIONS[0].name };
  const existingLineup = Array.isArray(t.lineup) ? t.lineup : [];
  const gkCountInLineup = existingLineup
    .map(num => players.find(p => p.number === num))
    .filter(p => p && p.position === 'GK').length;
  const needsRebuild = existingLineup.length !== LINEUP_SIZE || gkCountInLineup > 1;
  if (needsRebuild) {
    const { lineup, bench } = buildLineupForFormation(teamWithFormation);
    return { ...teamWithFormation, lineup, bench };
  }
  const benchNums = players.map(p => p.number).filter(n => !existingLineup.includes(n));
  return { ...teamWithFormation, lineup: existingLineup, bench: benchNums };
};
export const migrateGameState = (gs) => {
  if (!gs) return gs;
  let teams = (gs.teams || []).map(migrateTeam);
  // Re-distribute team ratings for pre-v4 saves (77-93 spread, only 2 over 90)
  if (!gs.version || gs.version < 4) {
    const ratings = buildTeamOvrDistribution();
    teams = teams.map((t, i) => {
      const target = ratings[i] ?? 80;
      const players = t.players.map((p, pi) => {
        let newOvr;
        if (pi < 11) newOvr = Math.max(55, Math.min(99, target + Math.floor(Math.random() * 9) - 4));
        else if (pi < 17) newOvr = Math.max(55, Math.min(99, target - 3 + Math.floor(Math.random() * 7) - 3));
        else newOvr = Math.max(55, Math.min(99, target - 7 + Math.floor(Math.random() * 7) - 3));
        return {
          ...p,
          overall: newOvr,
          seasonStartOverall: newOvr,
          potential: Math.max(newOvr, Math.min(99, newOvr + Math.floor(Math.random() * 10) + 2)),
          growthPoints: 0
        };
      });
      return { ...t, teamOvr: target, players };
    });
  }
  return {
    ...gs,
    teams,
    year: gs.year || (START_YEAR + (gs.season || 1) - 1),
    version: JWFL_VERSION
  };
};

// ============================================================
// Player Generation
// ============================================================
const generatePlayer = (number, teamId, rosterSize = ROSTER_SIZE) => {
  const positions = ['GK', 'CB', 'LB', 'RB', 'LWB', 'RWB', 'SW', 'CDM', 'CM', 'CAM', 'LM', 'RM', 'LW', 'RW', 'CF', 'ST', 'SS', 'LF', 'RF'];
  let primaryPosition;
  if (number <= 3) primaryPosition = 'GK';
  else if (number <= 11) primaryPosition = ['CB', 'CB', 'LB', 'RB', 'CB', 'LB', 'RB', 'LWB'][number - 4];
  else if (number <= 19) primaryPosition = ['CDM', 'CM', 'CM', 'CAM', 'LM', 'RM', 'CM', 'CDM'][number - 12];
  else primaryPosition = ['LW', 'ST', 'RW', 'ST', 'CF', 'RW'][Math.min(number - 20, 5)];
  if (!primaryPosition) primaryPosition = positions[Math.floor(Math.random() * positions.length)];

  const nationality = getRandomNationality();
  const name = generatePlayerName(nationality);
  const age = generateAge(primaryPosition);
  const overall = randomOverall();
  const potential = generatePotential(overall, age);

  return {
    id: `${teamId}-player-${number}`,
    number,
    name,
    position: primaryPosition,
    overall,
    age,
    nationality: nationality.country,
    flag: nationality.flag,
    potential,
    seasonStartOverall: overall,
    stats: EMPTY_STATS(),
    isRookie: age <= 19
  };
};

export const generatePlayerForMarket = (preferredPosition) => {
  const nationality = getRandomNationality();
  const name = generatePlayerName(nationality);
  const age = Math.floor(Math.random() * 16) + 18;
  const overall = Math.floor(Math.random() * 21) + 60;
  const potential = generatePotential(overall, age);
  return {
    id: `market-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    number: 0, name,
    position: preferredPosition || ['GK','CB','LB','RB','CDM','CM','CAM','LW','RW','ST'][Math.floor(Math.random() * 10)],
    overall, age,
    nationality: nationality.country,
    flag: nationality.flag,
    potential,
    seasonStartOverall: overall,
    stats: EMPTY_STATS(),
    isRookie: age <= 19
  };
};

const generateTeam = (cityIndex, teamOvrTarget) => {
  const city = ENGLISH_CITIES[cityIndex];
  const nickname = TEAM_NICKNAMES[cityIndex];
  const teamId = `team-${cityIndex + 1}`;
  const players = [];
  // Generate players whose overalls cluster around teamOvrTarget
  // Distribution: starters ~ target ± 5, bench ~ target - 6 to - 10
  for (let i = 1; i <= ROSTER_SIZE; i++) {
    const p = generatePlayer(i, teamId);
    // Override overall based on teamOvrTarget
    if (i <= 11) {
      // starters
      p.overall = Math.max(55, Math.min(99, teamOvrTarget + Math.floor(Math.random() * 9) - 4));
    } else if (i <= 17) {
      p.overall = Math.max(55, Math.min(99, teamOvrTarget - 3 + Math.floor(Math.random() * 7) - 3));
    } else {
      p.overall = Math.max(55, Math.min(99, teamOvrTarget - 7 + Math.floor(Math.random() * 7) - 3));
    }
    p.seasonStartOverall = p.overall;
    p.potential = generatePotential(p.overall, p.age);
    players.push(p);
  }
  const formation = FORMATIONS[Math.floor(Math.random() * FORMATIONS.length)];
  const base = {
    id: teamId,
    name: `${city} ${nickname}`,
    city,
    country: 'Japan',
    flag: '🇯🇵',
    teamOvr: teamOvrTarget,
    formation: formation.name,
    players,
    stats: { played: 0, won: 0, drawn: 0, lost: 0, goalsFor: 0, goalsAgainst: 0, points: 0 }
  };
  const { lineup, bench } = buildLineupForFormation(base);
  return { ...base, lineup, bench };
};

// Team ratings 77–93, only 2 teams >90
const buildTeamOvrDistribution = () => {
  const ratings = [];
  // 2 elite teams: 91-93
  ratings.push(91 + Math.floor(Math.random() * 3));
  ratings.push(91 + Math.floor(Math.random() * 3));
  // 6 strong: 85-89
  for (let i = 0; i < 6; i++) ratings.push(85 + Math.floor(Math.random() * 5));
  // 8 mid: 80-84
  for (let i = 0; i < 8; i++) ratings.push(80 + Math.floor(Math.random() * 5));
  // 4 lower: 77-79
  for (let i = 0; i < 4; i++) ratings.push(77 + Math.floor(Math.random() * 3));
  // Shuffle
  for (let i = ratings.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [ratings[i], ratings[j]] = [ratings[j], ratings[i]];
  }
  return ratings;
};

export const generateAllTeams = () => {
  const teams = [];
  const ovrs = buildTeamOvrDistribution();
  for (let i = 0; i < 20; i++) teams.push(generateTeam(i, ovrs[i]));
  return teams;
};

export const generateSeasonFixtures = (teams) => {
  const fixtures = [];
  const numTeams = teams.length;
  for (let week = 0; week < numTeams - 1; week++) {
    const weekFixtures = [];
    for (let i = 0; i < numTeams / 2; i++) {
      const home = (week + i) % (numTeams - 1);
      let away = (numTeams - 1 - i + week) % (numTeams - 1);
      if (i === 0) away = numTeams - 1;
      weekFixtures.push({
        id: `match-${week + 1}-${i + 1}`, week: week + 1,
        homeTeamId: teams[home].id, awayTeamId: teams[away].id,
        homeScore: null, awayScore: null, played: false, events: []
      });
    }
    fixtures.push(...weekFixtures);
  }
  const firstHalfFixtures = [...fixtures];
  firstHalfFixtures.forEach((fixture, idx) => {
    fixtures.push({
      id: `match-${Math.floor(idx / 10) + 20}-${(idx % 10) + 1}`,
      week: Math.floor(idx / 10) + 20,
      homeTeamId: fixture.awayTeamId, awayTeamId: fixture.homeTeamId,
      homeScore: null, awayScore: null, played: false, events: []
    });
  });
  return fixtures;
};

export const getMixedUpcomingForTeam = (teamId, gameState) => {
  if (!teamId || !gameState) return [];
  const out = [];

  (gameState.fixtures || []).forEach(f => {
    if (!f.played && (f.homeTeamId === teamId || f.awayTeamId === teamId)) {
      out.push({
        id: f.id, homeTeamId: f.homeTeamId, awayTeamId: f.awayTeamId,
        week: f.week, _comp: 'league', _compLabel: 'LGE', _compColor: '#FFD700',
        _sortKey: f.week * 100,
      });
    }
  });

  const cupGroups = gameState.cup?.groups || [];
  cupGroups.forEach((g, gi) => {
    (g.fixtures || []).forEach((f, fi) => {
      if (f.played) return;
      const involved = f.homeId === teamId || f.awayId === teamId;
      if (!involved) return;
      const pseudoWeek = Math.min(38, 4 + fi * 4);
      out.push({
        id: f.id || `cupgrp-${g.name}-${fi}`,
        homeTeamId: f.homeId, awayTeamId: f.awayId,
        week: pseudoWeek, _comp: 'cup', _compLabel: `CUP G${g.name}`, _compColor: '#FF3B30',
        _sortKey: pseudoWeek * 100 + 50,
      });
    });
  });

  const pushTie = (tie, label, color, sortBase) => {
    if (!tie || tie.winnerId) return;
    if (tie.homeId !== teamId && tie.awayId !== teamId) return;
    out.push({
      id: `${label}-${tie.homeId}-${tie.awayId}`,
      homeTeamId: tie.homeId, awayTeamId: tie.awayId,
      week: sortBase, _comp: label.toLowerCase(), _compLabel: label, _compColor: color,
      _sortKey: sortBase * 100 + 90,
    });
  };
  const ko = gameState.cup?.knockout;
  if (ko) {
    (ko.qf || []).forEach(t => pushTie(t, 'CUP QF', '#FF3B30', 28));
    (ko.sf || []).forEach(t => pushTie(t, 'CUP SF', '#FF3B30', 34));
    (ko.final || []).forEach(t => pushTie(t, 'CUP FINAL', '#FFD700', 38));
  }

  const ac = gameState.championsLeague;
  if (ac?.matches) ac.matches.forEach(f => { if (!f.played && (f.homeId === teamId || f.awayId === teamId)) out.push({ id: f.id, homeTeamId: f.homeId, awayTeamId: f.awayId, week: 10, _comp: 'championsLeague', _compLabel: 'UCL', _compColor: '#32D74B', _sortKey: 10 * 100 + 60 }); });
  if (ac?.rounds) ac.rounds.forEach(r => (r.matches || []).forEach(f => { if (!f.played && (f.homeId === teamId || f.awayId === teamId || f.homeTeamId === teamId || f.awayTeamId === teamId)) out.push({ id: f.id, homeTeamId: f.homeId || f.homeTeamId, awayTeamId: f.awayId || f.awayTeamId, week: 14, _comp: 'championsLeague', _compLabel: 'UCL KO', _compColor: '#32D74B', _sortKey: 14 * 100 + 70 }); }));

  const cwc = gameState.cwc;
  if (cwc?.rounds) cwc.rounds.forEach(r => (r.matches || []).forEach(f => { if (!f.played && (f.homeId === teamId || f.awayId === teamId || f.homeTeamId === teamId || f.awayTeamId === teamId)) out.push({ id: f.id, homeTeamId: f.homeId || f.homeTeamId, awayTeamId: f.awayId || f.awayTeamId, week: 20, _comp: 'cwc', _compLabel: 'CWC', _compColor: '#007AFF', _sortKey: 20 * 100 + 70 }); }));

  const wc = gameState.worldCup;
  if (wc?.rounds) wc.rounds.forEach(r => (r.matches || []).forEach(f => { if (!f.played && (f.homeId === teamId || f.awayId === teamId || f.homeTeamId === teamId || f.awayTeamId === teamId)) out.push({ id: f.id, homeTeamId: f.homeId || f.homeTeamId, awayTeamId: f.awayId || f.awayTeamId, week: 24, _comp: 'worldCup', _compLabel: 'WC', _compColor: '#AF52DE', _sortKey: 24 * 100 + 70 }); }));

  return out.sort((a, b) => a._sortKey - b._sortKey);
};

export const calculateStandings = (teams) => {
  return [...teams].sort((a, b) => {
    if (b.stats.points !== a.stats.points) return b.stats.points - a.stats.points;
    const gdA = a.stats.goalsFor - a.stats.goalsAgainst;
    const gdB = b.stats.goalsFor - b.stats.goalsAgainst;
    if (gdB !== gdA) return gdB - gdA;
    return b.stats.goalsFor - a.stats.goalsFor;
  });
};

export const positionCategory = (pos) => {
  if (pos === 'GK') return 'GK';
  if (['CB', 'LB', 'RB', 'LWB', 'RWB', 'SW'].includes(pos)) return 'DEF';
  if (['CDM', 'CM', 'CAM', 'LM', 'RM'].includes(pos)) return 'MID';
  return 'ATT';
};

export const getPositionCompatibility = (playerPos, slotPos) => {
  if (!playerPos || !slotPos) return 1;
  if (playerPos === slotPos) return 1.0;
  const pCat = positionCategory(playerPos);
  const sCat = positionCategory(slotPos);
  if (pCat === 'GK' || sCat === 'GK') return 0.35;
  const group = POSITION_GROUPS[slotPos] || [slotPos];
  if (group.includes(playerPos)) return 0.88;
  if (pCat === sCat) return 0.75;
  return 0.60;
};

export const getEffectiveOverall = (player, slotPosition) => {
  if (!player) return 0;
  const compat = getPositionCompatibility(player.position, slotPosition);
  return Math.round((player.overall || 70) * compat);
};

export const getSlotPositionFor = (team, playerNumber) => {
  if (!team?.lineup || !team?.formation) return null;
  const formation = FORMATIONS.find(f => f.name === team.formation) || FORMATIONS[0];
  const idx = team.lineup.findIndex(n => n === playerNumber);
  if (idx === -1) return null;
  return formation.positions[idx] || null;
};

export const getTeamOverall = (team) => {
  if (!team?.lineup || !team?.players) return 0;
  const formation = FORMATIONS.find(f => f.name === team.formation) || FORMATIONS[0];
  let total = 0;
  let count = 0;
  team.lineup.forEach((num, idx) => {
    const p = team.players.find(pp => pp.number === num);
    if (!p) return;
    const slot = formation.positions[idx] || p.position;
    total += getEffectiveOverall(p, slot);
    count += 1;
  });
  if (count === 0) return 0;
  return Math.round(total / count);
};

// ============================================================
// Initial Game State
// ============================================================
export const createInitialGameState = () => {
  const teams = generateAllTeams();
  const fixtures = generateSeasonFixtures(teams);
  return {
    teams, fixtures,
    currentWeek: 1,
    season: 1,
    year: START_YEAR,
    version: JWFL_VERSION,
    createdAt: new Date().toISOString(),
    lastSaved: null,
    championsLeagueTeams: null,
    cwcTeams: null,
  };
};

// Save / Load — scoped to the currently signed-in user
let _currentUserScope = 'anon';
export const setSaveScope = (userId) => { _currentUserScope = userId || 'anon'; };
export const getSaveScope = () => _currentUserScope;
const scopedKey = (slot) => `jwfl_save_${_currentUserScope}__${slot}`;

export const saveGame = (gameState, slotName = 'default') => {
  if (_currentUserScope === 'anon') {
    // Never write saves under the anon scope — prevents accidental loss when
    // logout state-clears fire before the scope flip propagates.
    return null;
  }
  const saveData = { ...gameState, lastSaved: new Date().toISOString() };
  try {
    localStorage.setItem(scopedKey(slotName), JSON.stringify(saveData));
  } catch (e) {
    console.error('Save failed (quota?):', e);
  }
  return saveData;
};
export const loadGame = (slotName = 'default') => {
  const saved = localStorage.getItem(scopedKey(slotName));
  if (!saved) return null;
  try {
    const parsed = JSON.parse(saved);
    return migrateGameState(parsed);
  } catch {
    return null;
  }
};
export const getSaveSlots = () => {
  const slots = [];
  const prefix = `jwfl_save_${_currentUserScope}__`;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(prefix)) {
      const slotName = key.replace(prefix, '');
      try {
        const data = JSON.parse(localStorage.getItem(key));
        slots.push({ name: slotName, lastSaved: data.lastSaved, season: data.season, currentWeek: data.currentWeek });
      } catch {}
    }
  }
  return slots;
};
export const deleteSaveSlot = (slotName) => localStorage.removeItem(scopedKey(slotName));
export const deleteAllSlotsForCurrentUser = () => {
  const prefix = `jwfl_save_${_currentUserScope}__`;
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k?.startsWith(prefix)) keys.push(k);
  }
  keys.forEach(k => localStorage.removeItem(k));
};

export const QUALIFICATION_ZONES = {
  FIFA_CLUB_WC: { start: 1, end: 2, label: 'FIFA Club World Cup', color: '#FFB400' },
  ASIAN_CUP: { start: 3, end: 4, label: 'Asian Cup', color: '#CCFF00' },
  NO_QUALIFICATION: { start: 5, end: 20, label: 'No Continental', color: '#4A5278' }
};

// Year/Season helpers - CWC every 4 years starting 2028
export const isCWCYear = (year) => year >= 2028 && (year - 2028) % 4 === 0;
export const nextCWCYear = (year) => {
  let y = 2028;
  while (y < year) y += 4;
  return y;
};
